import axios from "axios";
import React, { useEffect, useState } from "react";
import { translate } from "reducers/i18n";
import "./sms.css";
import settings from "client.settings.json";
import { PageComponent } from "components/page";
import { PseudoRadio } from "components/pseudoRadio";
let errorDurationLength = 15_000;

const Sms = (props) => {
  const {
    setError,
    messageId,
    authenticators,
    hideLoginInstruction,
    mappings,
    email,
    bgCol,
  } = props;

  const [codeRequested, setCodeRequested] = useState(null);
  const [codeValidated, setCodeValidated] = useState(null);
  const [isNotMyPhone, setIsNotMyPhone] = useState(null);
  const [selectedPhone, setSelectedPhone] = useState(authenticators[0]?._id);
  const [smsCode, setSmsCode] = useState("");
  const [smsCodeError, setSmsCodeError] = useState(null);
  const [showDoubleClickError, setShowDoubleClickError] = useState(false);
  const [coolingDown, setCoolingDown] = useState(false);
  const [startCount, setStartCount] = useState(15);
  const [count, setCount] = useState(15);
  
  useEffect(() => {
    if (!coolingDown) return;
    setCount(startCount);
    const interval = setInterval(() => {
      setCount((prev) => {
        if (prev === 1) {
          clearInterval(interval);
          setCoolingDown(false);
          setSmsCodeError(null);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [coolingDown]);

  const placeholder = mappings["E_DELIVERY_ENTER_CODE_PAGE_SMS"]?.placeholder?.replace(/<\/?[^>]+(>|$)/g, "") || "";
  
  const requestCode = async () => {
    if (showDoubleClickError === true) {
      setStartCount(30)
      setSmsCodeError("_error_resend_code");
      setCoolingDown(true)
    } else {
      setCodeRequested("requesting");
      try {
        await axios({
          url: settings.api + "/api/login/requestcode",
          method: "post",
          data: {
            messageId,
            authenticatorId: selectedPhone,
          },
        });
        setCodeRequested("requested");
      } catch (e) {
        // SMS rate limit hit; 15-second cooldown.
        if (e.response?.status === 429) {
          setStartCount(30)
          setSmsCodeError("_error_resend_code");
          setCoolingDown(true)
          return;
        }

        setError(e.message);
      }
    }
  };

  const notMyPhone = async () => {
    setIsNotMyPhone("confirming");
    try {
      await axios({
        url: settings.api + "/api/login/smslogin/isnotmyphone",
        method: "post",
        data: {
          messageId,
        },
      });
      setIsNotMyPhone("confirmed");
      hideLoginInstruction("phoneNumber");
    } catch (e) {
      setError(e.message);
    }
  };

  const validateCode = async () => {
    setCodeValidated("validating");
    setSmsCodeError(null);
    setCount(0);
    try {
      await axios({
        url: settings.api + "/api/login/validatecode",
        method: "post",
        data: {
          messageId,
          code: smsCode,
        },
      });
    } catch (error) {
      const { data, status } = error.response || {};
      const { count, errorcode } = data || {};
      setCodeValidated(null);

      // SMS rate limit hit; 15-second cooldown. OR Failed validation limit hit; 30-second cooldown.
      if (status === 429 && errorcode) {
        setStartCount(count || 15)
        setCoolingDown(true)
      }

      setSmsCodeError(errorcode || error.message);
    }
  };

  const btnFunctions = {
    E_DELIVERY_LOGIN_PAGE_SMS: [
      () => requestCode(),
      () => setIsNotMyPhone("needConfirmation"),
    ],
    E_DELIVERY_ENTER_CODE_PAGE_SMS: [
      () => validateCode(),
      () => {
        setShowDoubleClickError(true);
        setTimeout(() => {
          setShowDoubleClickError(false);
        }, errorDurationLength);
        requestCode();
      },
      () => {
        setCodeRequested(null);
        setShowDoubleClickError(false);
      },
    ],
    E_DELIVERY_NOTMYPHONE_PAGE: [
      () => notMyPhone(),
      () => setIsNotMyPhone(false),
    ],
    E_DELIVERY_NOTMYPHONE_PAGE_CONFIRMED: [],
  };

  const phoneList = (
    <ul className="PhoneSelect">
      {authenticators.map((phone) => (
        <li key={`phone_${phone._id}`}>
          <PseudoRadio condition={selectedPhone === phone._id} bgCol={bgCol}/>
          <input
            style={{ opacity: "0" }}
            onChange={(e) => {
              setSelectedPhone(e.target.value);
            }}
            checked={selectedPhone === phone._id}
            type="radio"
            value={phone._id}
            id={`phone_${phone._id}`}
            name="phonenumber"
          />{" "}
          &nbsp;
          <label htmlFor={`phone_${phone._id}`}>{phone.publicValue}</label>
        </li>
      ))}
    </ul>
  );

  const snippets = {
    "phone number":
      authenticators.find((phone) => phone._id === selectedPhone)
        ?.publicValue || "",
    "recepient email": email || "",
  };

  return (
    <>
      {(codeRequested === "requested" || codeRequested === "requesting") && (
        <>
          <PageComponent
            noHeader={true}
            content={mappings["E_DELIVERY_ENTER_CODE_PAGE_SMS"]}
            snippets={snippets}
            btnFunctions={btnFunctions["E_DELIVERY_ENTER_CODE_PAGE_SMS"]}
            disabled={!!codeValidated || coolingDown}
            disabledArray={[!smsCode, false, false]}
          >
            <p>
              <input
                className="code"
                autoFocus={true}
                type="text"
                inputMode="numeric"
                enterkeyhint={translate("_submit")}
                key="code"
                placeholder={placeholder}
                onChange={(e) => setSmsCode(e.target.value)}
                onKeyPress={(e) => {
                  if (!codeValidated && e.key === "Enter") validateCode();
                }}
                {...(smsCodeError
                  ? {
                      "aria-invalid": true,
                      "aria-describedby": "smsCodeError",
                    }
                  : {})}
              />
              {coolingDown ? (
                <span className="SmsCodeError" id="codeError">
                  {translate(smsCodeError, count)}
                </span>
              ) : (
                smsCodeError && (
                  <span className="SmsCodeError" id="smsCodeError">
                    {translate(smsCodeError)}
                  </span>
                )
              )}
            </p>
          </PageComponent>
        </>
      )}
      {isNotMyPhone && (
        <>
          {isNotMyPhone === "needConfirmation" ? (
            <PageComponent
              noHeader={true}
              content={mappings["E_DELIVERY_NOTMYPHONE_PAGE"]}
              snippets={snippets}
              btnFunctions={btnFunctions["E_DELIVERY_NOTMYPHONE_PAGE"]}
              disabled={!!codeValidated}
            />
          ) : (
            <PageComponent
              noHeader={true}
              content={mappings["E_DELIVERY_NOTMYPHONE_PAGE_CONFIRMED"]}
              snippets={snippets}
            />
          )}
        </>
      )}
      {codeRequested !== "requested" &&
        codeRequested !== "requesting" &&
        !isNotMyPhone && (
          <>
            <PageComponent
              noHeader={true}
              content={mappings["E_DELIVERY_LOGIN_PAGE_SMS"]}
              snippets={snippets}
              btnFunctions={btnFunctions["E_DELIVERY_LOGIN_PAGE_SMS"]}
              disabled={!!codeValidated}
            >
              {phoneList}
            </PageComponent>
          </>
        )}
    </>
  );
};

export default Sms;
